import { useEffect } from 'react';
import Box from '@mui/material/Box';
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { RootState, useAppSelector } from '@shared/store';
import { activeDirType } from '@shared/store/ducks/customizer/customizer.type';
import {
  SUBMENU_RAIL_WIDTH,
  SUBMENU_STROKE_WIDTH,
  buildInactiveTreePaths,
  buildSubmenuPath,
  getRailHeight,
} from './submenuPathGeometry';

const INACTIVE_STROKE = '#DFE5EF';

const lengthSpring = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 28,
  mass: 0.88,
};

const pathStrokeProps = {
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none' as const,
};

type MorphingSubmenuPathsProps = {
  branchYs: number[];
  activeIndex: number;
  activeColor: string;
};

export const MorphingSubmenuPaths = ({
  branchYs,
  activeIndex,
  activeColor,
}: MorphingSubmenuPathsProps) => {
  const customizer = useAppSelector((state: RootState) => state.customizer);
  const reduceMotion = useReducedMotion();
  const rtlFlip = customizer.activeDir === activeDirType.rtl ? 'scaleX(-1)' : 'scaleX(1)';

  const railHeight = getRailHeight(branchYs);
  const inactivePaths = buildInactiveTreePaths(branchYs);

  const targetBranchY =
    activeIndex >= 0 && activeIndex < branchYs.length ? branchYs[activeIndex] : (branchYs[0] ?? 0);

  const branchY = useMotionValue(targetBranchY);
  const pathD = useTransform(branchY, (y) => buildSubmenuPath(y));

  useEffect(() => {
    if (activeIndex < 0 || branchYs.length === 0) return;
    if (reduceMotion) {
      branchY.set(targetBranchY);
      return;
    }
    void animate(branchY, targetBranchY, lengthSpring);
  }, [activeIndex, targetBranchY, reduceMotion, branchY, branchYs.length]);

  if (branchYs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: SUBMENU_RAIL_WIDTH,
        height: railHeight,
        flexShrink: 0,
        pointerEvents: 'none',
        transform: rtlFlip,
      }}>
      <svg
        width={SUBMENU_RAIL_WIDTH}
        height={railHeight}
        viewBox={`0 0 ${SUBMENU_RAIL_WIDTH} ${railHeight}`}
        fill="none"
        style={{ display: 'block', overflow: 'visible' }}>
        {inactivePaths.map((d, index) => (
          <path
            key={`submenu-path-gray-${index}`}
            d={d}
            stroke={INACTIVE_STROKE}
            strokeWidth={SUBMENU_STROKE_WIDTH}
            {...pathStrokeProps}
          />
        ))}
        {activeIndex >= 0 &&
          (reduceMotion ? (
            <path
              d={buildSubmenuPath(targetBranchY)}
              stroke={activeColor}
              strokeWidth={SUBMENU_STROKE_WIDTH}
              {...pathStrokeProps}
            />
          ) : (
            <motion.path
              d={pathD}
              stroke={activeColor}
              strokeWidth={SUBMENU_STROKE_WIDTH}
              {...pathStrokeProps}
              style={{
                filter: `drop-shadow(0 0 2px ${activeColor}55)`,
              }}
            />
          ))}
      </svg>
    </Box>
  );
};
